import {
  Loader2,
  Pencil,
  RefreshCw,
  Search,
  UserCog,
  UserMinus,
  UserPlus,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { useAuth } from "@/contexts/AuthContext";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { TableActionsMenu } from "@/components/shared/TableActionsMenu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { userKeys, tenantMemberKeys } from "@/lib/queryKeys";
import {
  listPlatformUsers,
  listPendingRootInvites,
  inviteRootUser,
  resendRootInvite,
  cancelRootInvite,
  type PendingRootInvite,
  type PlatformRole,
} from "@/services/platformUsersService";
import {
  PlatformPendingInviteMobileCard,
  PlatformUserMobileCard,
} from "@/pages/users/platform-users-mobile-cards";
import { TenantUserMobileCard } from "@/pages/users/tenant-user-mobile-card";
import { TransferOwnershipCard } from "@/pages/users/TransferOwnershipCard";
import {
  listTenantMembers,
  listTenantPendingInvites,
  createTenantInvite,
  resendTenantInvite,
  cancelTenantInvite,
  updateMemberRole,
  type TenantInviteRole,
  type TenantMember,
  type TenantPendingInvite,
} from "@/services/tenantUsersService";

function statusVariant(
  status: string
): "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "active":
      return "success";
    case "pending":
      return "warning";
    case "disabled":
    case "expired":
      return "danger";
    default:
      return "info";
  }
}

function getMemberDisplayName(m: TenantMember): string {
  const parts = [m.firstName, m.lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (m.displayName?.trim()) return m.displayName;
  return m.email;
}

export function UsersPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeTenant } = useTenantSwitcher();
  const queryClient = useQueryClient();
  const pagination = usePaginationFromUrl();

  const isPlatform =
    user?.platformRole === "root" || user?.platformRole === "root_admin";
  const canAccessTenantUsers =
    user?.role === "owner" ||
    user?.role === "admin" ||
    user?.role === "accountant" ||
    isPlatform;
  const isTenantContext = Boolean(activeTenant?.id);
  const showTenantUsers = isTenantContext && canAccessTenantUsers;
  const showPlatformUsers = isPlatform && !isTenantContext;

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<PlatformRole | TenantInviteRole>(
    "root_admin"
  );
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [roleEditingUserId, setRoleEditingUserId] = useState<string | null>(null);

  const tenantId = activeTenant?.id ?? user?.tenantId ?? null;

  useEffect(() => {
    if (isTenantContext && !canAccessTenantUsers) {
      navigate("/dashboard", { replace: true });
    }
  }, [isTenantContext, canAccessTenantUsers, navigate]);

  if (!showPlatformUsers && !showTenantUsers) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (showTenantUsers && tenantId) {
    return (
      <TenantUsersContent
        tenantId={tenantId}
        pagination={pagination}
        inviteOpen={inviteOpen}
        setInviteOpen={setInviteOpen}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        inviteRole={inviteRole as TenantInviteRole}
        setInviteRole={(r) => setInviteRole(r)}
        cancellingId={cancellingId}
        setCancellingId={setCancellingId}
        resendingId={resendingId}
        setResendingId={setResendingId}
        roleEditingUserId={roleEditingUserId}
        setRoleEditingUserId={setRoleEditingUserId}
        queryClient={queryClient}
      />
    );
  }

  return (
    <PlatformUsersContent
      pagination={pagination}
      inviteOpen={inviteOpen}
      setInviteOpen={setInviteOpen}
      inviteEmail={inviteEmail}
      setInviteEmail={setInviteEmail}
      inviteRole={inviteRole as PlatformRole}
      setInviteRole={(r) => setInviteRole(r)}
      cancellingId={cancellingId}
      setCancellingId={setCancellingId}
      resendingId={resendingId}
      setResendingId={setResendingId}
      isRoot={user?.platformRole === "root"}
      queryClient={queryClient}
    />
  );
}

function PlatformUsersContent({
  pagination,
  inviteOpen,
  setInviteOpen,
  inviteEmail,
  setInviteEmail,
  inviteRole,
  setInviteRole,
  cancellingId,
  setCancellingId,
  resendingId,
  setResendingId,
  isRoot,
  queryClient,
}: {
  pagination: ReturnType<typeof usePaginationFromUrl>;
  inviteOpen: boolean;
  setInviteOpen: (v: boolean) => void;
  inviteEmail: string;
  setInviteEmail: (v: string) => void;
  inviteRole: PlatformRole;
  setInviteRole: (v: PlatformRole) => void;
  cancellingId: string | null;
  setCancellingId: (v: string | null) => void;
  resendingId: string | null;
  setResendingId: (v: string | null) => void;
  isRoot: boolean;
  queryClient: ReturnType<typeof useQueryClient>;
}): JSX.Element {
  const { t } = useTranslation();
  const {
    data: usersData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: userKeys.list({ page: pagination.page, limit: pagination.limit }),
    queryFn: () =>
      listPlatformUsers({ page: pagination.page, limit: pagination.limit }),
  });

  const { data: pendingInvites = [] } = useQuery({
    queryKey: userKeys.pendingInvites(),
    queryFn: listPendingRootInvites,
  });

  useEffect(() => {
    if (usersData != null) {
      pagination.setTotal(usersData.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [usersData, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(t("users.page.error"));
    }
  }, [isError, t]);

  const inviteMutation = useMutation({
    mutationFn: (payload: { email: string; platformRole: PlatformRole }) =>
      inviteRootUser(payload),
    onSuccess: () => {
      toast.success(t("users.invite.success"));
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("root_admin");
      queryClient.invalidateQueries({ queryKey: userKeys.pendingInvites() });
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
    onError: () => {
      toast.error(t("users.invite.error"));
    },
  });

  const cancelInviteMutation = useMutation({
    mutationFn: cancelRootInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.pendingInvites() });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: resendRootInvite,
    onSuccess: () => {
      toast.success(t("users.page.resendSuccess"));
      queryClient.invalidateQueries({ queryKey: userKeys.pendingInvites() });
    },
    onError: () => {
      toast.error(t("users.invite.error"));
    },
  });

  const handleInviteSubmit = (): void => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    inviteMutation.mutate({ email, platformRole: inviteRole });
  };

  const handleCancelInvite = (id: string): void => {
    setCancellingId(id);
    cancelInviteMutation.mutate(id, {
      onSettled: () => setCancellingId(null),
    });
  };

  const handleResendInvite = (id: string): void => {
    setResendingId(id);
    resendInviteMutation.mutate(id, {
      onSettled: () => setResendingId(null),
    });
  };

  const users = usersData?.data ?? [];
  const loading = isLoading;

  return (
    <div className="fx-content">
      <PageHeader
        eyebrow={t("users.page.eyebrow", "Plataforma · usuarios")}
        title={t("users.page.title")}
        description={t("users.page.description")}
        actions={
          isRoot ? (
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              {t("users.page.addUser")}
            </Button>
          ) : undefined
        }
      />
      <TableCard className="mt-4">
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : users.length === 0 ? (
          <EmptyState title={t("users.page.empty")} />
        ) : (
          <>
            <div className="space-y-3 py-2 md:hidden">
              {users.map((u) => (
                <PlatformUserMobileCard key={u.id} user={u} />
              ))}
            </div>
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("users.page.columns.name")}</TableHead>
                  <TableHead>{t("users.page.columns.email")}</TableHead>
                  <TableHead>{t("users.page.columns.role")}</TableHead>
                  <TableHead>{t("users.page.columns.status")}</TableHead>
                  <TableHead>{t("users.page.columns.addedBy")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      {u.displayName?.trim() || u.email}
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      {t(`users.page.role.${u.platformRole}`)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge variant={statusVariant(u.status)}>
                        {t(`users.page.status.${u.status}`)}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>—</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            {pagination.total > 0 && (
              <TablePagination
                pagination={pagination}
                rowsPerPageLabel={t("users.page.rowsPerPage")}
                pageOfLabel={t("users.page.pageOf", {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(
                    pagination.page * pagination.limit,
                    pagination.total
                  ),
                  total: pagination.total,
                })}
                onRefresh={() => void refetch()}
                isRefreshing={isFetching}
              />
            )}
          </>
        )}
      </TableCard>

      {pendingInvites.length > 0 && (
        <div className="mt-6">
          <h2 className="mb-2 text-lg font-medium text-foreground">
            {t("users.page.pendingInvites")}
          </h2>
          <TableCard>
            <div className="space-y-3 py-2 md:hidden">
              {pendingInvites.map((inv) => (
                <PlatformPendingInviteMobileCard
                  key={inv.id}
                  invite={inv}
                  isRoot={isRoot}
                  onResend={(id) => void handleResendInvite(id)}
                  onCancel={(id) => void handleCancelInvite(id)}
                  resendingId={resendingId}
                  cancellingId={cancellingId}
                />
              ))}
            </div>
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("users.page.columns.email")}</TableHead>
                  <TableHead>{t("users.page.columns.role")}</TableHead>
                  <TableHead>{t("users.page.columns.status")}</TableHead>
                  <TableHead>{t("users.page.invitedBy")}</TableHead>
                  <TableHead className="w-[180px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingInvites.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>{inv.email}</TableCell>
                    <TableCell>
                      {t(`users.page.role.${inv.platformRole}`)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        variant={
                          inv.expired ? "danger" : statusVariant("pending")
                        }
                      >
                        {inv.expired
                          ? t("users.page.status.expired")
                          : t("users.page.status.pending")}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      {inv.invitedByEmail ?? "—"}
                    </TableCell>
                    <TableCell>
                      {isRoot && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleResendInvite(inv.id)}
                            disabled={
                              resendingId === inv.id || cancellingId === inv.id
                            }
                          >
                            {resendingId === inv.id ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : null}
                            {t("users.page.resendInvite")}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleCancelInvite(inv.id)}
                            disabled={
                              cancellingId === inv.id || resendingId === inv.id
                            }
                          >
                            {t("users.page.cancelInvite")}
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </TableCard>
        </div>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("users.invite.title")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="invite-email">
                {t("users.invite.email")}
              </Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="invite-role">
                {t("users.invite.role")}
              </Label>
              <Select
                value={inviteRole}
                onValueChange={(v) => setInviteRole(v as PlatformRole)}
              >
                <SelectTrigger id="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="root">
                    {t("users.page.role.root")}
                  </SelectItem>
                  <SelectItem value="root_admin">
                    {t("users.page.role.root_admin")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteOpen(false)}
              disabled={inviteMutation.isPending}
            >
              {t("users.invite.cancel")}
            </Button>
            <Button
              onClick={() => void handleInviteSubmit()}
              disabled={!inviteEmail.trim() || inviteMutation.isPending}
            >
              {inviteMutation.isPending
                ? t("users.invite.submitting")
                : t("users.invite.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TenantUsersContent({
  tenantId,
  pagination,
  inviteOpen,
  setInviteOpen,
  inviteEmail,
  setInviteEmail,
  inviteRole,
  setInviteRole,
  cancellingId,
  setCancellingId,
  resendingId,
  setResendingId,
  roleEditingUserId,
  setRoleEditingUserId,
  queryClient,
}: {
  tenantId: string;
  pagination: ReturnType<typeof usePaginationFromUrl>;
  inviteOpen: boolean;
  setInviteOpen: (v: boolean) => void;
  inviteEmail: string;
  setInviteEmail: (v: string) => void;
  inviteRole: TenantInviteRole;
  setInviteRole: (v: TenantInviteRole) => void;
  cancellingId: string | null;
  setCancellingId: (v: string | null) => void;
  resendingId: string | null;
  setResendingId: (v: string | null) => void;
  roleEditingUserId: string | null;
  setRoleEditingUserId: (v: string | null) => void;
  queryClient: ReturnType<typeof useQueryClient>;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [searchInput, setSearchInput] = useState("");
  const [changeRoleSelectedRole, setChangeRoleSelectedRole] =
    useState<TenantInviteRole>("read_only");

  const {
    data: members = [],
    isLoading: loadingMembers,
    isFetching: fetchingMembers,
    refetch: refetchMembers,
  } = useQuery({
    queryKey: tenantMemberKeys.list(tenantId),
    queryFn: () => listTenantMembers(tenantId),
  });

  const {
    data: pendingInvites = [],
    refetch: refetchPendingInvites,
    isFetching: fetchingPendingInvites,
  } = useQuery({
    queryKey: tenantMemberKeys.pendingInvites(tenantId),
    queryFn: () => listTenantPendingInvites(tenantId),
  });

  const searchLower = searchInput.trim().toLowerCase();
  const filteredMembers = useMemo(() => {
    if (!searchLower) return members;
    return members.filter(
      (m) =>
        getMemberDisplayName(m).toLowerCase().includes(searchLower) ||
        m.email.toLowerCase().includes(searchLower)
    );
  }, [members, searchLower]);

  const filteredPendingInvites = useMemo(() => {
    if (!searchLower) return pendingInvites;
    return pendingInvites.filter((inv) =>
      inv.email.toLowerCase().includes(searchLower)
    );
  }, [pendingInvites, searchLower]);

  type TableRow =
    | { type: "member"; data: TenantMember }
    | { type: "invite"; data: TenantPendingInvite };

  const combinedRows = useMemo((): TableRow[] => {
    const memberRows: TableRow[] = filteredMembers.map((data) => ({
      type: "member",
      data,
    }));
    const inviteRows: TableRow[] = filteredPendingInvites.map((data) => ({
      type: "invite",
      data,
    }));
    return [...memberRows, ...inviteRows];
  }, [filteredMembers, filteredPendingInvites]);

  useEffect(() => {
    pagination.setTotal(combinedRows.length);
  }, [combinedRows.length, pagination]);

  useEffect(() => {
    if (searchLower && pagination.page > 1) {
      pagination.goToPage(1);
    }
  }, [searchLower]); // eslint-disable-line react-hooks/exhaustive-deps -- only reset page when search changes

  const { page, limit } = pagination;
  const paginatedRows = useMemo(
    () => combinedRows.slice((page - 1) * limit, page * limit),
    [combinedRows, page, limit]
  );

  const inviteMutation = useMutation({
    mutationFn: (payload: { email: string; role: TenantInviteRole }) =>
      createTenantInvite(tenantId, payload),
    onSuccess: () => {
      toast.success(t("users.tenant.inviteSuccess"));
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("read_only");
      queryClient.invalidateQueries({ queryKey: tenantMemberKeys.all(tenantId) });
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.pendingInvites(tenantId),
      });
    },
    onError: () => {
      toast.error(t("users.tenant.inviteError"));
    },
  });

  const cancelInviteMutation = useMutation({
    mutationFn: (inviteId: string) => cancelTenantInvite(tenantId, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.pendingInvites(tenantId),
      });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: (inviteId: string) =>
      resendTenantInvite(tenantId, inviteId),
    onSuccess: () => {
      toast.success(t("users.page.resendSuccess"));
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.pendingInvites(tenantId),
      });
    },
    onError: () => {
      toast.error(t("users.tenant.inviteError"));
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: TenantInviteRole;
    }) => updateMemberRole(tenantId, userId, role),
    onSuccess: () => {
      setRoleEditingUserId(null);
      queryClient.invalidateQueries({ queryKey: tenantMemberKeys.all(tenantId) });
      toast.success(t("users.tenant.roleUpdated"));
    },
    onError: () => {
      toast.error(t("users.tenant.roleUpdateError"));
    },
  });

  const handleResendTenantInvite = (inviteId: string): void => {
    setResendingId(inviteId);
    resendInviteMutation.mutate(inviteId, {
      onSettled: () => setResendingId(null),
    });
  };

  const memberBeingEdited = members.find(
    (m) => m.userId === roleEditingUserId
  );
  useEffect(() => {
    if (
      memberBeingEdited &&
      memberBeingEdited.role !== "owner" &&
      editableRoles.includes(memberBeingEdited.role as TenantInviteRole)
    ) {
      setChangeRoleSelectedRole(memberBeingEdited.role as TenantInviteRole);
    }
  }, [roleEditingUserId, memberBeingEdited?.role]);

  const handleInviteSubmit = (): void => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    inviteMutation.mutate({ email, role: inviteRole });
  };

  const handleCancelInvite = (id: string): void => {
    setCancellingId(id);
    cancelInviteMutation.mutate(id, {
      onSettled: () => setCancellingId(null),
    });
  };

  const editableRoles: TenantInviteRole[] = [
    "owner",
    "admin",
    "accountant",
    "read_only",
  ];
  const loading = loadingMembers;
  const hasNoData = !loading && combinedRows.length === 0;
  const refetchAll = (): void => {
    void refetchMembers();
    void refetchPendingInvites();
  };

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("users.tenant.eyebrow", "Tu equipo")}
        title={t("users.tenant.title")}
        description={t("users.tenant.description")}
        actions={
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            {t("users.tenant.addUser")}
          </Button>
        }
      />
      {isOwner && user?.accountType === "accountant" ? (
        <TransferOwnershipCard
          tenantId={tenantId}
          onTransferRequested={refetchAll}
        />
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-[300px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            placeholder={t("users.tenant.searchPlaceholder")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9"
            aria-label={t("users.tenant.searchPlaceholder")}
          />
        </div>
      </div>
      <TableCard>
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : hasNoData ? (
          <EmptyState
            title={t("users.tenant.empty")}
            message={t("users.tenant.emptyMessage")}
            action={
              <Button onClick={() => setInviteOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                {t("users.tenant.addUser")}
              </Button>
            }
          />
        ) : (
          <>
            <div className="space-y-3 py-2 md:hidden">
              {paginatedRows.length === 0 ? (
                <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                  {t("users.tenant.noSearchResults")}
                </p>
              ) : (
                paginatedRows.map((row) =>
                  row.type === "member" ? (
                    <TenantUserMobileCard
                      key={`m-${row.data.userId}`}
                      row={row}
                      getMemberDisplayName={getMemberDisplayName}
                      onCardClick={() =>
                        navigate(`/users/${row.data.userId}`, {
                          state: { member: row.data },
                        })
                      }
                      menu={
                        <TableActionsMenu
                          actions={
                            row.data.role === "owner"
                              ? [
                                  {
                                    label: t("users.tenant.editUser"),
                                    icon: Pencil,
                                    onClick: () =>
                                      navigate(`/users/${row.data.userId}`, {
                                        state: { member: row.data },
                                      }),
                                  },
                                ]
                              : [
                                  {
                                    label: t("users.tenant.editUser"),
                                    icon: Pencil,
                                    onClick: () =>
                                      navigate(`/users/${row.data.userId}`, {
                                        state: { member: row.data },
                                      }),
                                  },
                                  {
                                    label: t("users.tenant.changeRole"),
                                    icon: UserCog,
                                    onClick: () =>
                                      setRoleEditingUserId(row.data.userId),
                                  },
                                ]
                          }
                        />
                      }
                    />
                  ) : (
                    <TenantUserMobileCard
                      key={`i-${row.data.id}`}
                      row={row}
                      getMemberDisplayName={getMemberDisplayName}
                      onCardClick={() =>
                        navigate(`/users/invites/${row.data.id}`, {
                          state: { invite: row.data },
                        })
                      }
                      menu={
                        <TableActionsMenu
                          actions={[
                            {
                              label: t("users.page.resendInvite"),
                              icon: RefreshCw,
                              onClick: () =>
                                void handleResendTenantInvite(row.data.id),
                            },
                            {
                              label: t("users.page.cancelInvite"),
                              icon: UserMinus,
                              onClick: () =>
                                void handleCancelInvite(row.data.id),
                              variant: "destructive",
                            },
                          ]}
                        />
                      }
                    />
                  )
                )
              )}
            </div>
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("users.page.columns.name")}</TableHead>
                  <TableHead>{t("users.page.columns.email")}</TableHead>
                  <TableHead>{t("users.page.columns.role")}</TableHead>
                  <TableHead>{t("users.page.columns.status")}</TableHead>
                  <TableHead className="w-[80px]" aria-hidden />
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-muted-foreground"
                    >
                      {t("users.tenant.noSearchResults")}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) =>
                    row.type === "member" ? (
                      <TableRow
                        key={`member-${row.data.userId}`}
                        onRowClick={() =>
                          navigate(`/users/${row.data.userId}`, {
                            state: { member: row.data },
                          })
                        }
                      >
                        <TableCell>
                          {getMemberDisplayName(row.data)}
                        </TableCell>
                        <TableCell>{row.data.email}</TableCell>
                        <TableCell>
                          {t(`users.tenant.role.${row.data.role}`)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            variant={statusVariant(row.data.status)}
                          >
                            {t(`users.page.status.${row.data.status}`)}
                          </StatusBadge>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <TableActionsMenu
                            actions={
                              row.data.role === "owner"
                                ? [
                                    {
                                      label: t("users.tenant.editUser"),
                                      icon: Pencil,
                                      onClick: () =>
                                        navigate(
                                          `/users/${row.data.userId}`,
                                          { state: { member: row.data } }
                                        ),
                                    },
                                  ]
                                : [
                                    {
                                      label: t("users.tenant.editUser"),
                                      icon: Pencil,
                                      onClick: () =>
                                        navigate(
                                          `/users/${row.data.userId}`,
                                          { state: { member: row.data } }
                                        ),
                                    },
                                    {
                                      label: t("users.tenant.changeRole"),
                                      icon: UserCog,
                                      onClick: () =>
                                        setRoleEditingUserId(row.data.userId),
                                    },
                                  ]
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow
                        key={`invite-${row.data.id}`}
                        onRowClick={() =>
                          navigate(`/users/invites/${row.data.id}`, {
                            state: { invite: row.data },
                          })
                        }
                      >
                        <TableCell className="text-muted-foreground">—</TableCell>
                        <TableCell>{row.data.email}</TableCell>
                        <TableCell>
                          {t(`users.tenant.role.${row.data.role}`)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            variant={
                              row.data.expired
                                ? "danger"
                                : statusVariant("pending")
                            }
                          >
                            {row.data.expired
                              ? t("users.page.status.expired")
                              : t("users.page.status.pending")}
                          </StatusBadge>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <TableActionsMenu
                            actions={[
                              {
                                label: t("users.page.resendInvite"),
                                icon: RefreshCw,
                                onClick: () =>
                                  void handleResendTenantInvite(row.data.id),
                              },
                              {
                                label: t("users.page.cancelInvite"),
                                icon: UserMinus,
                                onClick: () =>
                                  void handleCancelInvite(row.data.id),
                                variant: "destructive",
                              },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  )
                )}
              </TableBody>
            </Table>
            </div>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("users.tenant.rowsPerPage")}
              pageOfLabel={t("users.tenant.pageOf", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(
                  pagination.page * pagination.limit,
                  pagination.total
                ),
                total: pagination.total,
              })}
              onRefresh={refetchAll}
              isRefreshing={fetchingMembers || fetchingPendingInvites}
            />
          </>
        )}
      </TableCard>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("users.tenant.inviteTitle")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="tenant-invite-email">
                {t("users.invite.email")}
              </Label>
              <Input
                id="tenant-invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="tenant-invite-role">
                {t("users.invite.role")}
              </Label>
              <Select
                value={inviteRole}
                onValueChange={(v) => setInviteRole(v as TenantInviteRole)}
              >
                <SelectTrigger id="tenant-invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">
                    {t("users.tenant.role.owner")}
                  </SelectItem>
                  <SelectItem value="admin">
                    {t("users.tenant.role.admin")}
                  </SelectItem>
                  <SelectItem value="accountant">
                    {t("users.tenant.role.accountant")}
                  </SelectItem>
                  <SelectItem value="read_only">
                    {t("users.tenant.role.read_only")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteOpen(false)}
              disabled={inviteMutation.isPending}
            >
              {t("users.invite.cancel")}
            </Button>
            <Button
              onClick={() => void handleInviteSubmit()}
              disabled={!inviteEmail.trim() || inviteMutation.isPending}
            >
              {inviteMutation.isPending
                ? t("users.invite.submitting")
                : t("users.invite.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={roleEditingUserId != null}
        onOpenChange={(open) => !open && setRoleEditingUserId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("users.tenant.changeRoleTitle")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {memberBeingEdited && (
              <p className="text-sm text-muted-foreground">
                {getMemberDisplayName(memberBeingEdited)} ({memberBeingEdited.email})
              </p>
            )}
            <div className="grid gap-2">
              <Label htmlFor="change-role-select">
                {t("users.invite.role")}
              </Label>
              <Select
                value={changeRoleSelectedRole}
                onValueChange={(v) =>
                  setChangeRoleSelectedRole(v as TenantInviteRole)
                }
              >
                <SelectTrigger id="change-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {editableRoles.map((r) => (
                    <SelectItem key={r} value={r}>
                      {t(`users.tenant.role.${r}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRoleEditingUserId(null)}
              disabled={updateRoleMutation.isPending}
            >
              {t("users.invite.cancel")}
            </Button>
            <Button
              onClick={() => {
                if (roleEditingUserId)
                  updateRoleMutation.mutate({
                    userId: roleEditingUserId,
                    role: changeRoleSelectedRole,
                  });
              }}
              disabled={updateRoleMutation.isPending}
            >
              {updateRoleMutation.isPending
                ? t("users.invite.submitting")
                : t("users.tenant.changeRoleSave")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
